import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAnalysisHistory, setCurrentAnalysis, updateAnalysisInHistory } from "../../redux/slices/analysisSlice";
import { Loader2, Search, MapPin, Map, Share2, Sparkles } from "lucide-react"; // Re-ordered to fix import mismatch
import PropertyDetailModal from "../Modal/PropertyDetailModal";
import MapModal from "../Modal/MapModal";
import SharePropertyModal from "../Modal/SharePropertyModal";
import { JURISDICTION_GROUPS } from "../../utils/jurisdictionData";
import { motion, AnimatePresence } from "framer-motion";
import {
  detectJurisdiction,
  getZoningMetric,
  getJurisdictionInfo
} from "../Modal/MapModal/utils/jurisdictionHelpers";
import ZoningMetricCell from "./ZoningMetricCell";
import EmptyState from "./EmptyState";
import { calculateFeasibilityScore, getScoreColor } from "../../utils/opportunityAnalysis";
import { cleanAddress, getAddressParts } from "../../utils/addressUtils";
import { API_BASE_URL as API_BASE } from "../../config/apiConfig";
import { toast } from "react-toastify";
import AINarrativeModal from "../Modal/AINarrativeModal";


const ListView = ({ onNewSearch, onNavigateToDashboard }) => {
  const dispatch = useDispatch();
  const { analysisHistory, totalAnalyses, isLoadingHistory, cityCounts, error } = useSelector(
    (state) => state.analysis
  );

  const [limit] = useState(20);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  // Map modal state
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // AI Narrative Modal state
  const [isAINarrativeModalOpen, setIsAINarrativeModalOpen] = useState(false);
  const [isGeneratingNarrative, setIsGeneratingNarrative] = useState(false);
  const [narrativeAnalysis, setNarrativeAnalysis] = useState(null);

  // Filter state
  const [selectedCounty, setSelectedCounty] = useState("All");
  const [selectedCity, setSelectedCity] = useState("All");

  useEffect(() => {
    console.log("🔍 [Filter Telemetry] County:", selectedCounty, "| City:", selectedCity);
  }, [selectedCounty, selectedCity]);

  const loaderRef = useRef(null);

  // Load data with pagination
  const loadMoreData = useCallback(async () => {
    if (isFetchingMore || analysisHistory.length >= totalAnalyses) return;

    setIsFetchingMore(true);
    try {
      await dispatch(
        fetchAnalysisHistory({ limit, offset: currentOffset })
      ).unwrap();
      setCurrentOffset((prev) => prev + limit);
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setIsFetchingMore(false);
    }
  }, [
    dispatch,
    limit,
    currentOffset,
    isFetchingMore,
    analysisHistory.length,
    totalAnalyses,
  ]);

  // Initial load - RESET everything on mount
  useEffect(() => {
    const loadInitialData = async () => {
      setCurrentOffset(0);
      setHasInitialLoad(false);

      try {
        await dispatch(fetchAnalysisHistory({ limit, offset: 0 })).unwrap();
        setCurrentOffset(limit);
        setHasInitialLoad(true);
      } catch (err) {
        console.error("Failed to load initial data:", err);
      }
    };

    loadInitialData();
  }, [dispatch, limit]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!hasInitialLoad) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (
          target.isIntersecting &&
          !isFetchingMore &&
          analysisHistory.length < totalAnalyses
        ) {
          loadMoreData();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      }
    );

    const currentLoader = loaderRef.current;
    if (currentLoader) {
      observer.observe(currentLoader);
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [loadMoreData, isFetchingMore, analysisHistory.length, totalAnalyses, hasInitialLoad]);

  const handleViewDetails = (analysis) => {
    // Dispatch selected analysis to Redux as the current analysis
    dispatch(setCurrentAnalysis({ data: analysis }));
    // Navigate to the dashboard-style search view where AnalysisResults is rendered
    onNavigateToDashboard('search');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAnalysis(null);
  };

  const handleViewMap = (analysis) => {
    setSelectedAnalysis(analysis);
    setIsMapModalOpen(true);
  };

  const handleCloseMapModal = () => {
    setIsMapModalOpen(false);
    setSelectedAnalysis(null);
  };

  const handleShare = (analysis) => {
    setSelectedAnalysis(analysis);
    setIsShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
    setSelectedAnalysis(null);
  };

  const handleOpenAINarrative = async (analysis) => {
    setNarrativeAnalysis(analysis);
    setIsAINarrativeModalOpen(true);
    
    // Auto-trigger generation if not present
    if (!analysis.notes?.ai_summary) {
      setIsGeneratingNarrative(true);
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE}/api/agent/generate-summary/${analysis.id}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          }
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.detail || "Failed to generate AI summary");
        }
        const res = await response.json();
        if (res.success && res.data) {
          dispatch(updateAnalysisInHistory(res.data));
          setNarrativeAnalysis(res.data);
          toast.success("AI Feasibility summary generated!");
        } else {
          throw new Error("Invalid response structure");
        }
      } catch (err) {
        toast.error(err.message || "Failed to generate summary");
        console.error("AI summary error:", err);
      } finally {
        setIsGeneratingNarrative(false);
      }
    }
  };

  const handleManualGenerateNarrative = async () => {
    if (!narrativeAnalysis?.id) return;
    setIsGeneratingNarrative(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}/api/agent/generate-summary/${narrativeAnalysis.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to generate AI summary");
      }
      const res = await response.json();
      if (res.success && res.data) {
        dispatch(updateAnalysisInHistory(res.data));
        setNarrativeAnalysis(res.data);
        toast.success("AI Feasibility summary generated!");
      } else {
        throw new Error("Invalid response structure");
      }
    } catch (err) {
      toast.error(err.message || "Failed to generate summary");
      console.error("AI summary error:", err);
    } finally {
      setIsGeneratingNarrative(false);
    }
  };

  const handleCloseAINarrativeModal = () => {
    setIsAINarrativeModalOpen(false);
    setNarrativeAnalysis(null);
  };


  const filteredHistory = useMemo(() => {
    let result = analysisHistory || [];
    const totalBefore = result.length;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item =>
        (item.address || "").toLowerCase().includes(term) ||
        (item.apn || "").toLowerCase().includes(term)
      );
    }

    if (selectedCounty !== "All") {
      result = result.filter(item => {
        const jur = detectJurisdiction(item.address);
        const countyData = JURISDICTION_GROUPS.find(group =>
          group.cities.some(city => city.toLowerCase().replace(/\s+/g, '_') === jur)
        );
        return countyData?.county === selectedCounty;
      });
    }

    if (selectedCity !== "All") {
      result = result.filter(item => {
        const jur = detectJurisdiction(item.address);
        return jur === selectedCity.toLowerCase().replace(/\s+/g, '_');
      });
    }

    console.log(`📊 [Filtering Update] Results: ${result.length}/${totalBefore} | Search: "${searchTerm}" | County: ${selectedCounty} | City: ${selectedCity}`);
    return result;
  }, [analysisHistory, searchTerm, selectedCounty, selectedCity]);


  const hasMore = analysisHistory.length < totalAnalyses;

  if (isLoadingHistory && analysisHistory.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">
              Property Analysis
            </h1>
            <p className="text-slate-600">Loading your property data...</p>
          </div>
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-16 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="animate-spin h-16 w-16 text-blue-600 mx-auto mb-4" />
              <p className="text-slate-600 text-lg font-medium">
                Loading properties...
              </p>
              <p className="text-slate-500 text-sm mt-2">
                Please wait while we fetch your data
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">
              Property Analysis
            </h1>
            <p className="text-slate-600">
              There was an error loading your properties
            </p>
          </div>
          <div className="bg-white rounded-3xl shadow-xl border border-red-200 p-16">
            <div className="text-center">
              <div className="bg-red-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <span className="text-red-600 text-3xl">⚠</span>
              </div>
              <p className="text-red-600 text-xl font-semibold mb-2">
                Error Loading Properties
              </p>
              <p className="text-slate-600 mb-8">{error}</p>
              <button
                onClick={() => {
                  setCurrentOffset(0);
                  setHasInitialLoad(false);
                  dispatch(fetchAnalysisHistory({ limit, offset: 0 }));
                }}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-xl hover:shadow-lg transition-all font-semibold"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] py-8">
      <div>

        {/* ── THE LIQUID ARCHIVE (V2) ── */}
        <div className="flex flex-col gap-5 mb-5">
          {/* Main Context Header */}
          <div className="flex flex-col gap-2">
            <h1 className="text-4xl font-black text-slate-900 tracking-tighter leading-none flex items-center gap-3">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-500">Property Analysis List</span>
              <div className="h-1.5 w-1.5 rounded-full bg-indigo-500 mt-2 animate-pulse" />
            </h1>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.5em] ml-1"> Regional Intelligence Hub / {totalAnalyses || 0} Assets </p>
          </div>

          {/* Unified Archive Bar (Search + Counties) */}
          <div className="bg-white/80 backdrop-blur-2xl border border-slate-200/60 p-1.5 rounded-xl shadow-2xl shadow-slate-200/40 flex flex-col md:flex-row items-center gap-2">

            {/* Integrated Search with Match Badge */}
            <div className="relative group flex-1 w-full">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-indigo-500 transition-colors z-20" />
              <input
                type="text"
                placeholder="Find anything..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-14 pr-32 py-3.5 bg-transparent text-sm font-semibold focus:outline-none placeholder:text-slate-300 text-slate-900 z-10"
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 bg-slate-900 px-4 py-2 rounded-md border border-slate-800 shadow-lg">
                <span className="text-[10px] font-black text-white tabular-nums">{filteredHistory.length}</span>
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-tighter">Matches</span>
              </div>
            </div>

            <div className="hidden md:block w-px h-10 bg-slate-200/60 mx-2" />

            {/* Minimalist County Selector (Compact "Space Way") */}
            <div className="flex items-center gap-1 p-1 flex-shrink-0">
              {["All", ...JURISDICTION_GROUPS.map(g => g.county)].map((county) => {
                const isActive = selectedCounty === county;

                return (
                  <button
                    key={county}
                    onClick={() => {
                      setSelectedCounty(county);
                      setSelectedCity("All");
                    }}
                    className={`relative px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-[0.15em] transition-all group overflow-hidden ${isActive ? "text-slate-900" : "text-slate-400 hover:text-slate-600"}`}
                  >
                    <span className="relative z-10">{county}</span>
                    {isActive && (
                      <motion.div
                        layoutId="activeZenDot"
                        className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.6)]"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Prism Arch Flow (V6 - Best of the Best) */}
          <AnimatePresence mode="wait">
            {selectedCounty !== "All" && (
              <motion.div
                key={selectedCounty}
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 1.05, y: 10 }}
                className="relative flex flex-wrap items-center justify-center gap-1 px-2 py-2 rounded-2xl border border-white/40 shadow-2xl overflow-hidden mx-auto mt-2 mb-2"
              >
                {/* Dynamic Background Glow */}
                <div className={`absolute inset-0 opacity-10 blur-xl transition-colors duration-700 ${JURISDICTION_GROUPS.find(g => g.county === selectedCounty)?.color || "bg-slate-400"}`} />
                <div className="absolute inset-0 bg-white/40 backdrop-blur-md z-0" />

                <div className="relative z-10 flex items-center gap-2 pr-4 mr-3 border-r border-slate-200/60">
                  <div className={`w-1 h-3 rounded-full ${JURISDICTION_GROUPS.find(g => g.county === selectedCounty)?.color || "bg-slate-400"}`} />
                  <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-900">{selectedCounty}</span>
                </div>

                <div className="relative z-10 flex flex-wrap items-center justify-center gap-1">
                  {JURISDICTION_GROUPS.find(g => g.county === selectedCounty)?.cities.map((city) => {
                    const isActive = selectedCity === city;
                    const cityKey = city.toLowerCase().replace(/\s+/g, '_');
                    const count = cityCounts[cityKey] || 0;
                    const isDisabled = count === 0;

                    return (
                      <motion.button
                        key={city}
                        whileHover={!isDisabled ? { scale: 1.05 } : {}}
                        whileTap={!isDisabled ? { scale: 0.95 } : {}}
                        onClick={() => !isDisabled && setSelectedCity(isActive ? "All" : city)}
                        disabled={isDisabled}
                        title={isDisabled ? "No properties analyzed in this city yet" : ""}
                        className={`relative px-4 py-2 text-[9px] font-black uppercase tracking-widest transition-all rounded-xl ${isActive ? "text-white" : isDisabled ? "text-slate-300 cursor-not-allowed opacity-50 grayscale" : "text-slate-500 hover:text-slate-900"}`}
                      >
                        <span className="relative z-20">{city} {count > 0 ? `(${count})` : '(0)'}</span>

                        {/* Liquid Selection Arch */}
                        {isActive && (
                          <motion.div
                            layoutId="liquidArch"
                            className={`absolute inset-0 shadow-lg z-10 rounded-xl ${JURISDICTION_GROUPS.find(g => g.county === selectedCounty)?.color || "bg-slate-900"}`}
                            transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
                          >
                            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-xl" />
                          </motion.div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── PROPERTY TABLE ── */}
        {analysisHistory.length === 0 ? (
          <EmptyState
            type="initial"
            onAction={onNewSearch}
          />
        ) : filteredHistory.length === 0 ? (
          <EmptyState
            type="filtered"
            onAction={onNewSearch}
            selectedCity={selectedCity}
          />
        ) : (
          <div className="bg-white rounded-[2rem] border border-slate-200/60 shadow-2xl shadow-slate-200/40 overflow-hidden">
            <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-350px)]">
              <table className="w-full border-collapse min-w-[1100px]">
                <thead className="sticky top-0 z-40">
                  <tr className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-900 border-b border-white/10 shadow-lg">
                    <th className="px-6 py-5 text-left text-[11px] font-black text-white uppercase tracking-[0.25em] w-[340px]">Property Details</th>
                    <th className="px-4 py-5 text-right text-[11px] font-black text-white uppercase tracking-[0.25em]">Acreage</th>
                    <th className="px-4 py-5 text-right text-[11px] font-black text-white uppercase tracking-[0.25em]">Slope Grade</th>
                    <th className="px-4 py-5 text-right text-[11px] font-black text-white uppercase tracking-[0.25em]">Zoning Metric</th>
                    <th className="px-4 py-5 text-right text-[11px] font-black text-white uppercase tracking-[0.25em] w-[140px]">Max Floor Area</th>
                    <th className="px-4 py-5 text-right text-[11px] font-black text-white uppercase tracking-[0.25em] w-[140px]">Max Dev Area</th>
                    <th className="px-4 py-5 text-center text-[11px] font-black text-white uppercase tracking-[0.25em]">Opportunity</th>
                    <th className="px-4 py-5 text-center text-[11px] font-black text-white uppercase tracking-[0.25em]">CDP Status</th>
                    <th className="px-6 py-5 text-right text-[11px] font-black text-white uppercase tracking-[0.25em]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100/50">
                  {filteredHistory.map((analysis, idx) => {
                    const jur = detectJurisdiction(analysis.address);
                    const jurInfo = getJurisdictionInfo(jur);
                    const mdaLabel = jurInfo?.mdaLabel || analysis.mda_label || 'MDA';
                    const mfaLabel = jurInfo?.mfaLabel || analysis.mfa_label || 'MFA';
                    const slope = analysis.avg_slope_percent_LAH;
                    const calcResult = getZoningMetric(jur, slope || 0, analysis.parcel_area_acres || 0, analysis.zone_code || "R1");

                    return (
                      <motion.tr
                        key={analysis.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        onClick={() => handleViewDetails(analysis)}
                        className="group transition-all cursor-pointer relative"
                      >
                        <td className="px-6 py-6 relative z-10">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100/50 flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-all shadow-sm shrink-0">
                              <MapPin className="w-6 h-6" />
                            </div>
                            <div className="min-w-0">
                              {(() => {
                                const parts = getAddressParts(analysis.address);
                                if (parts.length <= 1) {
                                  return (
                                    <div className="text-[13px] font-black text-slate-900 truncate tracking-tight group-hover:text-indigo-600 transition-colors">
                                      {parts[0] || "N/A"}
                                    </div>
                                  );
                                }
                                return (
                                  <div className="flex flex-col gap-1">
                                    <div className="text-[13px] font-black text-slate-900 truncate tracking-tight group-hover:text-indigo-600 transition-colors">
                                      {parts[0]}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1">
                                      {parts.slice(1).map((part, i) => (
                                        <span key={i} className="inline-flex items-center gap-1 text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100 leading-tight">
                                          <span className="text-indigo-300">+</span> {part}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                                  {analysis.apn || "NO APN"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-6 text-right relative z-10">
                          <div className="text-sm font-black text-slate-900">{analysis.parcel_area_acres?.toFixed(2) || "—"}</div>
                          <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Total Acres</div>
                        </td>
                        <td className="px-4 py-6 text-right relative z-10">
                          <div className={`text-sm font-black ${slope > 25 ? "text-rose-500" : slope > 15 ? "text-amber-500" : "text-emerald-500"}`}>
                            {slope?.toFixed(1) || "—"}%
                          </div>
                          <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Average Grade</div>
                        </td>
                        <ZoningMetricCell
                          analysis={analysis}
                          jur={jur}
                          calcResult={calcResult}
                          idx={idx}
                        />
                        <td className="px-4 py-6 text-right relative z-10">
                          <div className="flex flex-col items-end group/mfa cursor-default">
                             <span className="text-[14px] font-black text-slate-900 tabular-nums tracking-tighter group-hover:text-indigo-600 transition-colors">
                               {analysis.mfa_ft2?.toLocaleString() || "—"}
                             </span>
                             <span className="text-[7px] font-black text-indigo-500 uppercase tracking-widest mt-0.5">{mfaLabel}</span>
                          </div>
                        </td>
                        <td className="px-4 py-6 text-right relative z-10">
                          <div className="flex flex-col items-end group/mda cursor-default">
                             <span className="text-[14px] font-black text-slate-900 tabular-nums tracking-tighter group-hover:text-slate-600 transition-colors">
                               {analysis.mda_ft2?.toLocaleString() || "—"}
                             </span>
                             <span className="text-[7px] font-black text-slate-500 uppercase tracking-widest mt-0.5">{mdaLabel}</span>
                          </div>
                        </td>
                        <td className="px-4 py-6 text-center relative z-10">
                          {(() => {
                            const score = calculateFeasibilityScore(analysis);
                            const colorClass = getScoreColor(score);
                            return (
                              <div className="flex flex-col items-center">
                                <div className={`px-2.5 py-1 rounded-lg font-black text-[11px] uppercase border shadow-sm ${
                                  colorClass === 'emerald' ? 'bg-emerald-50 text-emerald-700 border-emerald-100 shadow-emerald-100' :
                                  colorClass === 'amber' ? 'bg-amber-50 text-amber-700 border-amber-100 shadow-amber-100' :
                                  'bg-rose-50 text-rose-700 border-rose-100 shadow-rose-100'
                                }`}>
                                  {score}%
                                </div>
                                <div className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter mt-1">Feasibility</div>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-6 text-center relative z-10">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-black text-[9px] uppercase border tracking-widest transition-all ${analysis.requires_cdp
                            ? "bg-rose-50 text-rose-600 border-rose-100 shadow-sm shadow-rose-100"
                            : "bg-emerald-50 text-emerald-600 border-emerald-100 shadow-sm shadow-emerald-100"
                            }`}>
                            <div className={`w-1 h-1 rounded-full ${analysis.requires_cdp ? "bg-rose-400 animate-pulse" : "bg-emerald-400"}`} />
                            {analysis.requires_cdp ? "CDP Required" : "Standard"}
                          </span>
                        </td>
                        <td className="px-6 py-6 relative z-10">
                          <div className="flex items-center justify-end gap-2 transition-all">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleOpenAINarrative(analysis); }}
                              className="p-2.5 bg-white rounded-xl border border-slate-200 text-slate-400 hover:text-violet-600 hover:border-violet-200 hover:shadow-lg transition-all"
                              title="AI Feasibility Narrative"
                            >
                              <Sparkles className="w-4 h-4 text-violet-500" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleShare(analysis); }} className="p-2.5 bg-white rounded-xl border border-slate-200 text-slate-400 hover:text-indigo-600 hover:border-indigo-200 hover:shadow-lg transition-all"><Share2 className="w-4 h-4" /></button>

                            <button onClick={(e) => { e.stopPropagation(); handleViewMap(analysis); }} className="p-2.5 bg-white rounded-xl border border-slate-200 text-slate-400 hover:text-emerald-600 hover:border-emerald-200 hover:shadow-lg transition-all"><Map className="w-4 h-4" /></button>
                            <button onClick={(e) => { e.stopPropagation(); handleViewDetails(analysis); }} className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-indigo-100 hover:scale-105 transition-all">Report</button>
                          </div>
                        </td>
                        {/* Hover Backdrop Overlay */}
                        <td className="absolute inset-x-2 inset-y-1.5 bg-slate-50/80 backdrop-blur-sm rounded-2xl border border-slate-200/50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-0" colSpan="7" />
                      </motion.tr>
                    );
                  })}
                  {hasMore && (
                    <tr ref={loaderRef}>
                      <td colSpan="8" className={`text-center transition-all duration-500 ${(isFetchingMore && !searchTerm && selectedCounty === "All" && selectedCity === "All") ? "py-20 opacity-100" : "py-0 opacity-0"}`}>
                        <div className="flex flex-col items-center">
                          <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mx-auto" />
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-4">Syncing Portfolio Data...</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-8 py-5 bg-slate-50/50 border-t border-slate-200/60 flex items-center justify-between">
              <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                Showing <span className="text-slate-900">{filteredHistory.length}</span> / <span className="text-slate-900">{totalAnalyses}</span> Regions
              </span>
              {!hasMore && analysisHistory.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-600 rounded-lg border border-emerald-100">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span className="text-[9px] font-black uppercase tracking-tight">Full Portfolio Sync Enabled</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Property Detail Modal */}
      <PropertyDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        analysis={selectedAnalysis}
      />

      <MapModal
        isOpen={isMapModalOpen}
        onClose={handleCloseMapModal}
        address={cleanAddress(selectedAnalysis?.address)}
        apn={selectedAnalysis?.apn}
        county={selectedAnalysis?.county}
        sr_wkid={selectedAnalysis?.sr_wkid}
        parcelAreaAcres={selectedAnalysis?.parcel_area_acres}
        mda={selectedAnalysis?.mda_ft2}
        mfa={selectedAnalysis?.mfa_ft2}
        grossBuildingArea={selectedAnalysis?.gross_building_area_ft2}
        livingArea={selectedAnalysis?.living_area_ft2}
        avgSlope={selectedAnalysis?.avg_slope_percent_LAH}
        analysisId={selectedAnalysis?.id}
      />

      <SharePropertyModal
        isOpen={isShareModalOpen}
        onClose={handleCloseShareModal}
        propertyAddress={cleanAddress(selectedAnalysis?.address)}
        propertyId={selectedAnalysis?.id}
      />

      <AINarrativeModal
        isOpen={isAINarrativeModalOpen}
        onClose={handleCloseAINarrativeModal}
        analysis={narrativeAnalysis}
        onGenerate={handleManualGenerateNarrative}
        isGenerating={isGeneratingNarrative}
      />
    </div>

  );
};

export default ListView;
